import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  Character,
  RollPlus,
  CharacterSheetInterface,
  RolledValue,
  CreatureInstance,
  CharacterAdvantage,
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";
import Roller from "@dndComponents/other/Roller";
import DandDButtonBox from "@dndComponents/DandDButtonBox";

interface Props { 
  character: Character | CreatureInstance;
  char_interface: CharacterSheetInterface;
  disabled?: boolean;
}

const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CharacterInitiative({ 
  character,
  char_interface,
  disabled = false,
}: Props) {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(null);
  const [initiative_roll, setInitiativeRoll] = useState<RolledValue[]>([]);
  const [mod, setMod] = useState(0);
  const [adv, setAdv] = useState(0);
  const [advs, setAdvs] = useState<CharacterAdvantage[]>([]);
  const [gr_adv, setGrAdv] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setTimeout(() => {
      let advantage = 0;
      let advantages: CharacterAdvantage[] = [];
      let greater_advantage = false;
      if (character instanceof Character) {
        const { advantage: adv, advantages: advs, greater_advantage: gr_adv2 } = char_util.getAdvantage(character, "Initiative", null);
        advantage = adv;
        advantages = advs;
        greater_advantage = gr_adv2;
      }
      setMod(char_interface.initiative_modifier);
      setAdv(advantage);
      setAdvs(advantages);
      setGrAdv(greater_advantage);
    }, 100);
  }, [character, char_interface]);

  useEffect(() => {
    character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    recalculated();
  }, [character, recalculated]);

  const renderRoll = () =>{
    if (popoverAnchorEl !== null) {
      return (
        <Roller 
          name="Initiative"
          rolled_values={initiative_roll}
          type="Initiative" 
        />
      );
    }
    return null;
  }

  return (
    <>
      <Grid
        className="centered"
        size={{
          xs: 12
        }}>
        <DandDButtonBox 
          name={common_util.addPlusMaybe(mod)}
          style={{ 
            fontSize: "24px", 
            width: "50px",
            fontWeight: "bold" 
          }}
          advantage={ adv }
          advantages={ advs }
          greater_advantage={ gr_adv }
          disabled={disabled}
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            const rp = new RollPlus();
            rp.flat = mod;
            const roll = char_util.rollD20({ char_interface, rolls: [rp], type: "Initiative", use_ability_score: "DEX", advantage: char_interface.advantage, greater_advantage: gr_adv });
            setInitiativeRoll(roll);
            setPopoverAnchorEl(event.currentTarget);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 12
        }}>
        <div className="centered" style={{
          fontSize: "11px",
          fontWeight: "bold",
        }}>
          Initiative
        </div>
      </Grid>
      <Popover key="rolls"
        open={ popoverAnchorEl !== null }
        anchorEl={popoverAnchorEl}
        onClose={() => {
          setPopoverAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        { renderRoll() }
      </Popover>
    </>
  );
}
